export const RUSSIAN_FEATURE_NAME = 'russian-locale';

export const name = 'rnLocale';
/**
 * @ngdoc service
 * @name rhino.components.locale.service:rnLocale
 * @description This service provides the client application with the ability to query the server for the list of available
 * locales for a given user (browser) side language.
 */
export default function rnLocale(
  $log,
  $q,
  $window,
  $location,
  rnPreferences,
  rnLocaleCookie,
  rnClient
) {
  //noinspection BadExpressionStatementJS
  'ngInject';
  const defaultLocale = {
    id: '1',
    value: 'en-US',
    shortName: 'en',
    language: 'English (US)',
    appId: 'general',
  };

  const PRINTOS_DOC_LOCALES = {
    'en-EN': 'EN',
    'de-DE': 'DE',
    'en-GB': 'EN',
    'en-US': 'EN',
    'es-ES': 'ES',
    'fr-FR': 'FR',
    'it-IT': 'IT',
    'ja-JP': 'JP',
    'ko-KO': 'KO',
    'pt-PT': 'PT',
    'ru-RU': 'RU',
    'zh-HANS': 'ZHCN'
  };

  /**
   * This is the externally facing API of the service
   */
  const service = {
    allLocaleOptions,
    localeOptions,
    browserLocale,
    setLocalePreference,
    getLocalePreference,
    findBestLocale,
    getBestFit,
    getBestFitForBrowser,
    getUrlWithCurrentLocale,
    defaultLocale,
    getBcp47Locale,
  };
  return service;

  /////////////////////
  // Private functions
  ////////////////////

  /**
   * @ngdoc function
   * @name allLocaleOptions
   * @methodOf rhino.components.locale.service:rnLocale
   * @description Returns a full list of unfiltered locale options. Typically, `localeOptions()`
   * is the correct method to call because it filters out locales based on features.
   * However, in some cases, the full list of locales is necessary (for instance, when accepting
   * an invitation).
   *
   * @returns {*} a promise which, when resolved will be an array of locale objects
   */
  function allLocaleOptions() {
    return rnClient.getConfig().then(clientConfig => {
      return _.get(clientConfig, 'preferences.options.locales');
    });
  }

  /**
   * @ngdoc function
   * @name localeOptions
   * @methodOf rhino.components.locale.service:rnLocale
   * @description Query the server for the list of available locales, falling back to
   * the default locale on error and filtering out any locales the user/org doesn't have access to.
   *
   * @returns {*} a promise which, when resolved will be an array of locale objects
   */
  function localeOptions() {
    return rnClient.getConfig().then(clientConfig => {
      let locales = _.get(clientConfig, 'preferences.options.locales');
      let features = clientConfig.features || [];
      let cookieLocale = rnLocaleCookie.getLocale();
      locales = _.filter(locales, locale => {
        if (!locale.features) {
          return true;
        }
        // Include a locale if it's in the cookie. This allows for showing a locale at sign-in
        // that the user previously had access to.
        if (locale.value === cookieLocale) {
          return true;
        }
        return _.intersection(locale.features, features).length > 0;
      });
      return locales;
    }).catch(() => {
      return [defaultLocale];
    });
  }

  /**
   * @ngdoc function
   * @name browserLocale
   * @methodOf rhino.components.locale.service:rnLocale
   * @description Get the w3c language code from the browser.  This represents the language that the borwser is currently configured
   * to use
   * @returns {string|*} the language code
   */
  function browserLocale() {
    var lang = $window.navigator.language || $window.navigator.browserLanguage || $window.navigator.userLanguage;
    if(lang === undefined) {
      lang = $window.navigator.languages ? $window.navigator.languages[0] : null;
    }
    return lang;
  }

  /**
   * @ngdoc function
   * @name findBestLocale
   * @methodOf rhino.components.locale.service:rnLocale
   * @description Given the list of available locales and a language code, try to select the best fit
   * locale.  Language fit will attempt to start out as specific as possible and will become more
   * generic until a suitable match is found
   * @param {object|Array} availableLocales an array of the available locales as provided by the server for the application
   * @param {String} lang code
   * @returns {value: string, name: string} the best fit language
   */
  function findBestLocale(availableLocales = [], lang = undefined) {
    //now try to match a language object from the service...
    //try an exact match first..
    $log.debug('Match browser locale. available:', availableLocales);

    if (lang) {
      let exactLocale = availableLocales.find(locale => locale.value === lang);
      if (exactLocale) {
        return exactLocale;
      }

      //failing an exact match, try a highest level language match... (i.e. the first part... en-us would match en)
      let langShortName = getShortName(lang).toLowerCase();
      let langLocale = availableLocales.find(locale => getShortName(locale.value).toLowerCase() === langShortName);
      if (langLocale) {
        return langLocale;
      }
    }

    // Default China domain to Chinese (Simplified)
    if ($location.host().endsWith('.cn')) {
      let chinese = availableLocales.find(locale => locale.value === 'zh-HANS');
      if (chinese) {
        return chinese;
      }
    }

    //worst case, we could not match anything
    return defaultLocale;
  }

  /**
   * @ngdoc function
   * @name getBestFit
   * @methodOf rhino.components.locale.service:rnLocale
   * @description  Returns the best fit locale for the user using the following heuristic,
   *   1. Use the user's locale preference, if available.
   *   2. If a browser cookie is set indicating the locale (in case the user isn't signed in), it will be used.
   *   3. Get a list of available locales from the server.
   *      Attempt to find the best fit locale based on the browser.
   *   4. Fallback to the default locale.
   *
   * @returns {IPromise<string>|Promise.<string>} promise of the locale value with a shortName for convenience
   */
  function getBestFit() {
    return getLocalePreference().then(locale => {
      if(locale) {
        return withShortName(locale);
      }

      return getBestFitForBrowser();
    });
  }

  function getLocalePreference() {
    return allLocaleOptions().then((locales) => {
      let locale;
      let cookieLocale = rnLocaleCookie.getLocale();
      if (cookieLocale) {
        locale = _.find(locales, 'value', cookieLocale);
        return locale;
      }

      return rnPreferences.getAll().then((prefs) => {
        let prefLocale = _.get(prefs, 'general.locale');
        if (prefLocale) {
          locale = _.find(locales, 'value', prefLocale);
          return locale;
        }
      });
    });
  }

  function setLocalePreference(locale, force = false) {
    return getLocalePreference().then(prefLocale => {
      if ( force || !prefLocale || !_.get(prefLocale, 'features') || (locale.value === _.get(prefLocale, 'value')) ) {
        // Only set the preference if the current preference isn't associated with a feature.
        // This keeps sign-in from overwriting a preference that was only available after sign-in.
        return rnPreferences.setSingle('general', 'locale', locale.value);
      }
      return rnPreferences.getAll();
    });
  }

  function withShortName(locale) {
    locale.shortName = getShortName(locale.value);
    return locale;
  }

  function getShortName(value) {
    return value.split('-')[0];
  }

  function getUrlWithCurrentLocale(base, extension){
    let currentLocale = rnLocaleCookie.getLocale() || 'en-US';
    return base+PRINTOS_DOC_LOCALES[currentLocale]+extension;
  }

  function getBestFitForBrowser() {
    return localeOptions()
      .then(localeOptions => {
        let bestLocale = findBestLocale(localeOptions, browserLocale());
        return withShortName(bestLocale);
      });
  }

  // BCP47 is a standard locale with lang-REGION
  function getBcp47Locale() {
    return getBestFit().then(locale => {
      let value = locale.value;
      if (value === 'zh-HANS') {
        value = 'zh-CN';
      }
      return value;
    });
  }
}



// WEBPACK FOOTER //
// ../~/@printos/rhino/src/components/locale/locale.service.js